import { choqueDe, diaNegocio, horaLocal, instanteLocal, MENSAJE_SIN_SERVICIO, serviciosDe, type CitaVista, type DetallesChoque, type SlotDisponible } from '@barbones/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Zap } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, ErrorApi, esEncolada, mensajeError } from '@/api/cliente';
import { Button } from '@/components/ui/button';
import { Campo, Input, Select, Textarea } from '@/components/ui/form';
import { Dialogo } from '@/components/ui/overlay';
import { diaLargo } from '@/lib/utils';
import { SelectorCliente, type SeleccionCliente } from '../clientes/SelectorCliente';
import { useBarberos, useServicios } from './datos';
import { PanelChoque } from './PanelChoque';
import { VerAgendaDialog } from './VerAgendaDialog';
import { SelectorHorario } from './SelectorHorario';
import { ServiciosCita } from './ServiciosCita';

interface Props {
  dia: string;
  barberoInicial?: string | null;
  horaInicial?: string;
  onCerrar: () => void;
  onCreada?: (cita: CitaVista) => void;
  /** Cita a editar: el mismo formulario precargado con sus datos. */
  cita?: CitaVista;
}

const SIN_BARBERO = '';

function erroresDeApi(error: unknown): Record<string, string> {
  if (!(error instanceof ErrorApi) || !Array.isArray(error.details)) return {};
  return Object.fromEntries(
    (error.details as { path?: (string | number)[]; message: string }[]).map((d) => [(d.path ?? []).join('.'), d.message]),
  );
}

/** Cliente de la cita en el formato del selector (solo usa nombre, teléfono y contadores). */
function clienteDeCita(cita: CitaVista): SeleccionCliente {
  return {
    tipo: 'existente',
    cliente: { ...cita.cliente, fechaNacimiento: null, origen: '', origenDetalle: null, notas: null, ultimaVisita: null },
  };
}

/** Alta de cita (también para clientes sin cita: "Siguiente disponible") y edición de una existente. */
export function NuevaCitaDialog({ dia: diaInicial, barberoInicial, horaInicial, onCerrar, onCreada, cita: original }: Props) {
  const qc = useQueryClient();
  const servicios = useServicios();
  const editando = !!original;
  /** Cita ya cobrada: se corrige sin tocar lo cobrado, dentro de su día y siempre con barbero. */
  const finalizada = original?.estado === 'finalizada';
  const [cliente, setCliente] = useState<SeleccionCliente>(original ? clienteDeCita(original) : null);
  const [servicioIds, setServicioIds] = useState<string[]>(original ? serviciosDe(original).map((s) => s.id) : ['']);
  const [errorServicio, setErrorServicio] = useState<string | undefined>();
  const [barberoId, setBarberoId] = useState<string>(original ? (original.barberoId ?? SIN_BARBERO) : (barberoInicial ?? SIN_BARBERO));
  const [preferidoId, setPreferidoId] = useState(original && !original.barberoId ? (original.barberoPreferidoId ?? '') : '');
  const [dia, setDia] = useState(original ? diaNegocio(new Date(original.inicio)) : diaInicial);
  const barberos = useBarberos(dia);
  const [hora, setHora] = useState(original ? horaLocal(new Date(original.inicio)) : (horaInicial ?? ''));
  /** Aviso cuando el barbero elegido no trabaja el nuevo día y la cita pasa a "Sin asignar". */
  const [aviso, setAviso] = useState<string | null>(null);
  const nombres = useRef(new Map<string, string>());
  for (const b of barberos.data ?? []) nombres.current.set(b.id, b.nombre);

  // Si el barbero no trabaja (o no recibe citas) el día elegido, la cita se agenda sin barbero
  // conservando la hora que ya se había escogido; se asigna después.
  useEffect(() => {
    if (!barberoId || !barberos.data || barberos.isFetching) return;
    if (barberos.data.some((b) => b.id === barberoId)) return;
    const nombre = nombres.current.get(barberoId) ?? 'El barbero';
    setAviso(`${nombre} no trabaja el ${diaLargo(dia)}. La cita quedará sin barbero y se asigna después.`);
    setPreferidoId('');
    setBarberoId(SIN_BARBERO);
  }, [barberoId, barberos.data, barberos.isFetching, dia]);
  const [notas, setNotas] = useState(original?.notas ?? '');
  /** La hora chocó con otra cita del barbero: huecos antes y después para elegir. */
  const [choque, setChoque] = useState<DetallesChoque | undefined>();
  const [verAgenda, setVerAgenda] = useState(false);
  const serviciosListos = servicioIds.every(Boolean);
  const idsElegidos = serviciosListos ? servicioIds.join(',') : '';

  const siguiente = useMutation({
    mutationFn: () =>
      api<{ slot: SlotDisponible | null }>('/agenda/siguiente-disponible', { query: { servicioIds: idsElegidos, barberoId, excluirCitaId: original?.id } }),
    onSuccess: ({ slot }) => {
      if (!slot) return toast.warning('No hay horarios libres para ese barbero en el periodo permitido');
      setDia(diaNegocio(new Date(slot.inicio)));
      setHora(horaLocal(new Date(slot.inicio)));
    },
  });

  const crear = useMutation({
    /** `horaElegida`: la opción Antes/Después, que se guarda sin esperar a que cambie el estado. */
    mutationFn: (horaElegida: string | void) => {
      const datos = {
        ...(cliente?.tipo === 'existente'
          ? { clienteId: cliente.cliente.id }
          : cliente?.tipo === 'nuevo'
            ? { clienteNuevo: { ...cliente.datos, origenDetalle: cliente.datos.origenDetalle || undefined } }
            : {}),
        servicioIds,
        barberoId: barberoId || null,
        barberoPreferidoId: barberoId || preferidoId || null,
        inicio: instanteLocal(dia, horaElegida || hora).toISOString(),
      };
      return original
        ? api<CitaVista>(finalizada ? `/citas/${original.id}/finalizada` : `/citas/${original.id}`, { method: 'PATCH', body: { ...datos, notas: notas.trim() || null } })
        : api<CitaVista>('/citas', {
            method: 'POST',
            body: { ...datos, origen: dia === diaNegocio(new Date()) ? 'walk_in' : 'web', notas: notas || undefined },
          });
    },
    onSuccess: (cita) => {
      toast.success(editando ? `Cita de ${cita.cliente.nombre} actualizada` : `Cita agendada para ${cita.cliente.nombre}`);
      void qc.invalidateQueries({ queryKey: ['agenda'] });
      void qc.invalidateQueries({ queryKey: ['pendientes'] });
      onCreada?.(cita);
      onCerrar();
    },
    onError: (e) => {
      if (esEncolada(e)) {
        toast.info(mensajeError(e));
        onCerrar();
        return;
      }
      setChoque(choqueDe(e) ?? undefined);
    },
  });
  /** La hora, el día o el barbero cambiaron: el choque y su mensaje ya no aplican. */
  const limpiarChoque = () => {
    if (!choque) return;
    setChoque(undefined);
    crear.reset();
  };
  const errores = erroresDeApi(crear.error);
  const listo = cliente && dia && hora && (!finalizada || barberoId);
  const agendar = () => {
    if (!serviciosListos) return setErrorServicio(MENSAJE_SIN_SERVICIO);
    crear.mutate();
  };

  return (
    <Dialogo
      abierto
      ancho="lg"
      onCambiarAbierto={(v) => !v && onCerrar()}
      titulo={finalizada ? 'Editar servicio' : editando ? 'Editar detalles de la cita' : 'Nueva cita'}
      descripcion={
        finalizada
          ? 'Corrige lo que se registró mal. Lo cobrado no cambia.'
          : editando
            ? 'Cambia los datos que necesites y guarda.'
            : 'Las citas se confirman automáticamente.'
      }
      pie={
        <>
          <Button variante="secundario" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button onClick={agendar} cargando={crear.isPending} disabled={!listo}>
            {editando ? 'Guardar cambios' : 'Agendar cita'}
          </Button>
        </>
      }
    >
      <div className="grid gap-5 md:grid-cols-2">
        <section className="flex flex-col gap-4">
          <div>
            <p className="mb-1.5 text-[13px] font-medium">{editando ? 'Cambiar cliente de la cita' : 'Cliente'}</p>
            <SelectorCliente valor={cliente} onCambiar={setCliente} errores={errores} />
          </div>
          <ServiciosCita
            servicios={servicios.data ?? []}
            valor={servicioIds}
            onCambiar={(ids) => {
              setServicioIds(ids);
              setErrorServicio(undefined);
              limpiarChoque();
            }}
            error={errorServicio ?? errores.servicioIds}
            extra={original ? serviciosDe(original) : undefined}
            etiqueta={editando ? 'Cambiar servicio' : 'Servicio'}
          />
          <Campo
            etiqueta={editando ? 'Cambiar barbero' : 'Barbero'}
            ayuda="Solo aparecen quienes trabajan ese día y pueden recibir citas."
          >
            {(p) => (
              <Select
                {...p}
                value={barberoId}
                onChange={(e) => {
                  setBarberoId(e.target.value);
                  limpiarChoque();
                  setAviso(null);
                }}
              >
                {finalizada ? (
                  <option value={SIN_BARBERO}>Seleccione el barbero</option>
                ) : (
                  <option value={SIN_BARBERO}>Sin asignar (se asigna después)</option>
                )}
                {barberos.data?.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          {aviso && (
            <p role="status" className="-mt-2 rounded-md bg-warning-soft px-3 py-2 text-[13px] text-warning">
              {aviso}
            </p>
          )}
          {!barberoId && !finalizada && (
            <Campo etiqueta="Barbero de preferencia (opcional)" ayuda="Se toma en cuenta al armar los horarios.">
              {(p) => (
                <Select {...p} value={preferidoId} onChange={(e) => setPreferidoId(e.target.value)}>
                  <option value="">Sin preferencia</option>
                  {barberos.data?.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.nombre}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>
          )}
          <Campo etiqueta="Notas (opcional)">
            {(p) => <Textarea {...p} value={notas} onChange={(e) => setNotas(e.target.value)} maxLength={500} />}
          </Campo>
        </section>

        {finalizada ? (
          <section className="flex flex-col gap-3">
            <p role="note" className="rounded-md bg-warning-soft px-3 py-2.5 text-[13px] text-warning">
              El total cobrado y sus pagos se conservan. Si cambias los servicios, lo cobrado se reparte entre los nuevos según su precio y
              se recalcula la comisión. Para corregir el monto, usa «Corregir cobro» en Caja.
            </p>
            <div>
              <p className="text-[12px] text-foreground-muted">Fecha</p>
              <p className="text-[13.5px] first-letter:uppercase">{diaLargo(dia)}</p>
            </div>
            <Campo etiqueta="Cambiar hora" requerido ayuda="Solo dentro del mismo día.">
              {(p) => <Input {...p} type="time" step={300} value={hora} onChange={(e) => setHora(e.target.value)} />}
            </Campo>
            {crear.error && !Object.keys(errores).length && (
              <p role="alert" className="text-[13px] text-destructive">
                {mensajeError(crear.error)}
              </p>
            )}
          </section>
        ) : (
        <section className="flex flex-col gap-3">
          {barberoId && (
            <Button variante="secundario" onClick={() => siguiente.mutate()} disabled={!serviciosListos} cargando={siguiente.isPending} className="self-start">
              <Zap /> Siguiente horario disponible
            </Button>
          )}
          {choque && barberoId && (
            <PanelChoque
              barbero={nombres.current.get(barberoId) ?? 'el barbero'}
              choque={choque}
              ocupado={crear.isPending}
              onElegir={(h) => {
                setHora(h);
                crear.mutate(h);
              }}
              onVerAgenda={() => setVerAgenda(true)}
            />
          )}
          {verAgenda && barberoId && (
            <VerAgendaDialog
              dia={dia}
              barberoId={barberoId}
              excluirCitaId={original?.id}
              onHora={(h) => {
                setHora(h);
                limpiarChoque();
              }}
              onCerrar={() => setVerAgenda(false)}
            />
          )}
          <SelectorHorario
            barberoId={barberoId || null}
            dia={dia}
            onDia={(d) => {
              setDia(d);
              setAviso(null);
              // La hora escrita se conserva (spec §1); la API avisa al guardar si ese día ya no cabe.
              limpiarChoque();
            }}
            hora={hora}
            onHora={(h) => {
              setHora(h);
              limpiarChoque();
            }}
            etiquetaFecha={editando ? 'Cambiar fecha' : undefined}
            etiquetaHora={editando ? 'Cambiar hora' : undefined}
            horaActual={original && dia === diaNegocio(new Date(original.inicio)) ? horaLocal(new Date(original.inicio)) : undefined}
          />
          {crear.error && !Object.keys(errores).length && !choque && (
            <p role="alert" className="text-[13px] text-destructive">
              {mensajeError(crear.error)}
            </p>
          )}
        </section>
        )}
      </div>
    </Dialogo>
  );
}
